import { elakay_API } from "../../config";
import { isAuthenticated } from "../auth";
import { elakayAutoText } from "../pageInside/dataElakay";
 
const getToken = () => isAuthenticated()?.token || "";

const buildQuery = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      query.set(key, value);
    }
  });
  return query.toString();
};

// A refused request may come back as an HTML page (nginx 413, proxy
// errors): always turn it into a readable message instead of "Unexpected
// token <" or a bare status code.
const errorMessageFor = (response, data, fallbackPrefix) => {
  if (data && typeof data.error === "string" && data.error) return data.error;
  if (response.status === 413) {
    return elakayAutoText(
      "howWeDoIt_api_tooLarge",
      "Contenu trop volumineux pour le serveur (limite actuelle de la production : 1 Mo par requete). Reduisez le contenu ou faites relever la limite nginx."
    );
  }
  if (response.status === 401) return elakayAutoText("howWeDoIt_api_unauthorized", "Session expiree : reconnectez-vous.");
  if (response.status === 403) return elakayAutoText("howWeDoIt_api_forbidden", "Action reservee a l'equipe Elakay.");
  if (response.status >= 500) return elakayAutoText("howWeDoIt_api_server", "Le serveur est indisponible pour le moment. Reessayez dans un instant.");
  return `${fallbackPrefix} ${response.status}`;
};

const apiFetch = async (path, { method = "GET", body, token } = {}) => {
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  const resolvedToken = token || getToken();
  if (resolvedToken) headers.Authorization = `Bearer ${resolvedToken}`;

  const response = await fetch(`${elakay_API}${path}`, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(errorMessageFor(response, data, "Erreur How We Do It"));
    error.status = response.status;
    error.code = data?.code;
    error.data = data;
    throw error;
  }
  return data;
};

const uploadFile = async (endpoint, file, token) => {
  const formData = new FormData();
  formData.append("file", file);

  const headers = { Accept: "application/json" };
  const resolvedToken = token || getToken();
  if (resolvedToken) headers.Authorization = `Bearer ${resolvedToken}`;

  const response = await fetch(`${elakay_API}${endpoint}`, {
    method: "POST",
    headers,
    body: formData,
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(errorMessageFor(response, data, "Erreur upload"));
    error.status = response.status;
    throw error;
  }
  return data;
};

// ---------------------------------------------------------------------------
// Public
// ---------------------------------------------------------------------------

export const getTutorials = (params = {}, token) => {
  const query = buildQuery(params);
  return apiFetch(`/how-we-do-it/public${query ? `?${query}` : ""}`, { token });
};

export const getAllTutorials = async (params = {}, token) => {
  const pageSize = 60;
  let skip = 0;
  let total = Infinity;
  const tutorials = [];
  let categories = [];
  while (skip < total) {
    const data = await getTutorials({ ...params, limit: pageSize, skip }, token);
    const page = data.tutorials || [];
    if (!categories.length) categories = data.categories || [];
    tutorials.push(...page);
    total = Number(data.count) || 0;
    if (!page.length) break;
    skip += page.length;
  }
  return { tutorials, categories, count: tutorials.length };
};

export const getTutorial = (slug, { preview } = {}, token) => {
  const query = buildQuery({ preview: preview ? "1" : "" });
  return apiFetch(`/how-we-do-it/public/slug/${encodeURIComponent(slug)}${query ? `?${query}` : ""}`, {
    token,
  });
};

export const getPublicCategories = (token) => apiFetch("/how-we-do-it/public/categories", { token });
export const getTutorialLanguages = (token) => apiFetch("/how-we-do-it/languages", { token });

// ---------------------------------------------------------------------------
// Admin — tutorials
// ---------------------------------------------------------------------------

export const getAdminTutorials = (params = {}, token) => {
  const query = buildQuery(params);
  return apiFetch(`/how-we-do-it/admin${query ? `?${query}` : ""}`, { token });
};

export const getAdminTutorial = (id, token) => apiFetch(`/how-we-do-it/admin/${id}`, { token });

export const createTutorial = (payload, token) =>
  apiFetch("/how-we-do-it/admin", { method: "POST", body: payload, token });

export const updateTutorial = (id, payload, token) =>
  apiFetch(`/how-we-do-it/admin/${id}`, { method: "PUT", body: payload, token });

export const autosaveTutorial = (id, payload, token) =>
  apiFetch(`/how-we-do-it/admin/${id}/autosave`, { method: "PATCH", body: payload, token });

export const changeTutorialStatus = (id, status, token) =>
  apiFetch(`/how-we-do-it/admin/${id}/status`, { method: "PATCH", body: { status }, token });

export const duplicateTutorial = (id, token) =>
  apiFetch(`/how-we-do-it/admin/${id}/duplicate`, { method: "PATCH", token });

// `orderedIds`: the rows shown (possibly a filtered subset) in their new
// order; the server keeps every other tutorial in place.
export const reorderTutorials = (orderedIds, token) =>
  apiFetch("/how-we-do-it/admin/reorder", { method: "PATCH", body: { orderedIds }, token });

// Quick dashboard toggle (featured) through the same allow-listed autosave.
export const setTutorialFeatured = (id, featured, token) =>
  apiFetch(`/how-we-do-it/admin/${id}/autosave`, { method: "PATCH", body: { featured: !!featured }, token });

export const deleteTutorial = (id, token) =>
  apiFetch(`/how-we-do-it/admin/${id}`, { method: "DELETE", token });

// ---------------------------------------------------------------------------
// Admin — categories
// ---------------------------------------------------------------------------

export const getCategories = (token) => apiFetch("/how-we-do-it/admin/categories", { token });

export const createCategory = (payload, token) =>
  apiFetch("/how-we-do-it/admin/categories", { method: "POST", body: payload, token });

export const updateCategory = (id, payload, token) =>
  apiFetch(`/how-we-do-it/admin/categories/${id}`, { method: "PUT", body: payload, token });

export const deleteCategory = (id, token) =>
  apiFetch(`/how-we-do-it/admin/categories/${id}`, { method: "DELETE", token });

export const reorderCategories = (items, token) =>
  apiFetch("/how-we-do-it/admin/categories/reorder", { method: "PATCH", body: { items }, token });

// ---------------------------------------------------------------------------
// Media
// ---------------------------------------------------------------------------

export const uploadHowWeDoItImage = (file, token) =>
  uploadFile("/how-we-do-it/media/upload-image", file, token);

export const uploadHowWeDoItVideo = (file, token) =>
  uploadFile("/how-we-do-it/media/upload-video", file, token);

export const uploadHowWeDoItFile = (file, token) =>
  uploadFile("/how-we-do-it/media/upload-file", file, token);

export const getMediaLibrary = async (type = "image", token) => {
  const query = buildQuery({ type });
  const centralQuery = buildQuery({ kind: type === "video" ? "video" : "image", limit: 100, sort: "newest" });
  const [local, central] = await Promise.all([
    apiFetch(`/how-we-do-it/media/library${query ? `?${query}` : ""}`, { token }),
    apiFetch(`/media-library?${centralQuery}`, { token }).catch(() => ({ assets: [] })),
  ]);
  const files = [...(local.files || [])];
  const known = new Set(files.map((item) => item.path || item.url));
  (central.assets || []).forEach((asset) => {
    if (!asset.url || known.has(asset.url)) return;
    known.add(asset.url);
    files.push({
      filename: asset.originalName || asset.name,
      name: asset.name,
      path: asset.url,
      url: asset.url,
      assetId: asset._id,
      mimeType: asset.mimeType,
      thumbnailUrl: asset.thumbnailUrl,
      central: true,
    });
  });
  return { ...local, files };
};

const STORED_MEDIA_PATH = /^\/data\/(images|videos|files)\/how-we-do-it\/([^/?#]+)$/;

/**
 * Deletes an uploaded file (stored /data/<folder>/how-we-do-it/<file> path)
 * from the media library. Without `force`, a file still used by another
 * tutorial is refused with error.code "MEDIA_IN_USE" and error.data.usedBy.
 */
export const deleteHowWeDoItMedia = (mediaPath, { force = false, excludeTutorialId } = {}, token) => {
  const match = STORED_MEDIA_PATH.exec(String(mediaPath || ""));
  if (!match) return Promise.reject(new Error(elakayAutoText("howWeDoIt_media_notDeletable", "Seuls les fichiers importes dans Elakay peuvent etre supprimes.")));
  const query = buildQuery({ force: force ? "1" : "", excludeTutorialId });
  return apiFetch(`/how-we-do-it/media/file/${match[1]}/${encodeURIComponent(match[2])}${query ? `?${query}` : ""}`, { method: "DELETE", token });
};
